import { Tag } from '@creditornot/cb-tag';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { CourierRestriction } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { ConfirmModal } from 'common/components';
import { usePluralization } from 'common/hooks';
import useCurrentUser from 'common/hooks/useCurrentUser';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { useBulkChangeDeliveryTracking } from 'modules/shared-menu/telemetry';
import { OfferingApiError, OfferWithSelectionAndAuthor } from 'modules/shared-menu/types';

import BulkActionErrorList from '../BulkActionErrorList';
import { buildUpdateOffersPayload } from '../transformers';
import { useBatchUpdateOffers } from '../useBatchUpdateOffers';
import { updateLocalSelectionData } from '../utils';

export type ModalType = 'disableBikeDelivery' | 'disableCarDelivery' | 'disableMotorcycleDelivery';

type Props = {
  onClose: () => void;
  selectedIds: Set<string>;
  modalType: ModalType;
};

const StyledConfirmModal = styled(ConfirmModal)`
  width: 480px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const modalTypeToTitleMap: Record<Props['modalType'], string> = {
  disableBikeDelivery: 'menu.bulkActions.courierRestrictions.disableBikeDelivery',
  disableCarDelivery: 'menu.bulkActions.courierRestrictions.disableCarDelivery',
  disableMotorcycleDelivery: 'menu.bulkActions.courierRestrictions.disableMotorcycleDelivery',
};

const modalTypeToCourierRestrictionMap: Record<Props['modalType'], CourierRestriction> = {
  disableBikeDelivery: 'no_bikes',
  disableCarDelivery: 'no_cars',
  disableMotorcycleDelivery: 'no_motorcycles',
};

const BulkActionModalTitle = ({ count, title }: { count: number; title: React.ReactNode }) => {
  return (
    <TitleContainer>
      {title}
      <Tag variant="lightBlue" size="small" style={{ marginInlineStart: '8px', textTransform: 'uppercase' }}>
        <FormattedMessage
          id="menu.bulkActions.itemCount"
          values={{
            count,
          }}
        />
      </Tag>
    </TitleContainer>
  );
};

const BulkCourierRestrictionModal = ({ selectedIds, modalType, onClose }: Props) => {
  const intl = useIntl();
  const { offers, updateSelectionData, selection } = useSelectionData();
  const { executeBatchUpdateOffers, isLoading } = useBatchUpdateOffers();
  const { formatPluralMessage } = usePluralization();
  const { data: user } = useCurrentUser();
  const trackDeliveryMethodChange = useBulkChangeDeliveryTracking([...selectedIds]);

  const onSuccess = useCallback(
    (successfullyUpdatedOffers: OfferWithSelectionAndAuthor[]) => {
      trackDeliveryMethodChange(modalType);
      toast.success(
        formatPluralMessage({
          id: 'menu.bulkActions.connectProductToCatalog.successToastMessage',
          count: successfullyUpdatedOffers.length,
        }),
      );
      updateLocalSelectionData(successfullyUpdatedOffers, updateSelectionData);
    },
    [formatPluralMessage, modalType, trackDeliveryMethodChange, updateSelectionData],
  );

  const onError = useCallback(
    (errors: OfferingApiError[]) => {
      toast.error(
        <BulkActionErrorList
          allOffers={offers}
          errors={errors}
          selectionPrimaryLanguage={selection.primary_language}
          description={intl.formatMessage({ id: 'shared-menu.bulk-actions.toasts.update.error-description' })}
        />,
        { duration: Infinity },
      );
    },
    [intl, offers, selection.primary_language],
  );

  const onConfirm = useCallback(async () => {
    const newRestriction = modalTypeToCourierRestrictionMap[modalType];
    const offersToUpdate = offers
      .filter(offer => selectedIds.has(offer.id))
      .map(offer => {
        const courierRestrictions = offer?.courier_restrictions || [];
        return {
          ...offer,
          courier_restrictions: courierRestrictions.includes(newRestriction)
            ? courierRestrictions
            : [...courierRestrictions, newRestriction],
        };
      });
    const offerPayloads = buildUpdateOffersPayload(offersToUpdate, selection.id, user?._id.$oid ?? '');
    await executeBatchUpdateOffers({ offerPayloads, onSuccess, onError });
    onClose();
  }, [
    executeBatchUpdateOffers,
    modalType,
    offers,
    onClose,
    onError,
    onSuccess,
    selectedIds,
    selection.id,
    user?._id.$oid,
  ]);

  return (
    <StyledConfirmModal
      open
      cancelText={<FormattedMessage id="state.cancel" />}
      confirmText={<FormattedMessage id="state.save" />}
      description={<FormattedMessage id="menu.bulkActions.courierRestrictions.confirmDescription" />}
      header={
        <BulkActionModalTitle
          count={selectedIds.size}
          title={intl.formatMessage({ id: modalTypeToTitleMap[modalType] })}
        />
      }
      loading={isLoading}
      onCancel={onClose}
      onConfirm={onConfirm}
    />
  );
};

export default BulkCourierRestrictionModal;
